/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 14:55:08
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  TextStyle,
  Button,
  FormLayout,
  SkeletonDisplayText,
  SkeletonBodyText,
  InlineError,
  Link,
  Heading,
  Icon
} from '@shopify/polaris';
import { Field, reduxForm, SubmissionError, getFormValues } from 'redux-form';
import type { FormProps } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { ChevronLeftMinor } from '@shopify/polaris-icons';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { URL } from 'src/constants/links';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import ConfirmRunSetupModal from 'src/components/views/theme-setup/theme-setup-form/confirm-run-setup-modal';
import { StepProgress } from 'src/components/views/theme-setup/step-progress';
import ThemeErrorModal from 'src/components/containers/common/theme-error-modal';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { updateLocalTenantApp } from 'actions/oauth-actions';
import { checkShopifyPaidPlan } from 'src/helpers/subscription-helper';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';
import ThemeTurboWarning, {
  WarningType
} from 'src/components/views/theme-setup-v3/theme-setup-form/theme-turbo-warning';
import { isThemeTurbo } from 'src/helpers/theme-setup-helper';
import { ExternalMinor } from '@shopify/polaris-icons';

type Props = {
  theme: Object,
  themes: boolean,
  getAllThemesLoading: boolean,
  isTrialTheme: boolean,
  inputThemeName: string,
  outputThemeName: string,
  isOS20: boolean,
  themeSetupV2: boolean,
  checkThemeLoading: boolean,
  setupThemeLoading: boolean,
  setupThemeSuccess: boolean,
  setupThemeError: any,
  getAllThemes: () => void,
  setupTheme: (data: { theme: string, layout: string }) => void,
  unmountComponent: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  step: number,
  activeModalConfirmSetup: boolean,
  activeModalError: boolean,
  dataSetup: Object
};

class ThemeSetup extends Component<ComposedProps, State> {
  state = {
    step: 0,
    activeModalConfirmSetup: false,
    activeModalError: false,
    dataSetup: {}
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.checkThemeLoading === true && this.props.checkThemeLoading === false) {
      startSABLoading();
    }

    if (nextProps.checkThemeLoading === false && this.props.checkThemeLoading === true) {
      stopSABLoading();
    }

    // Action after run setup
    if (this.props.setupThemeLoading === true && nextProps.setupThemeLoading === false) {
      // Error
      // Show error modal
      if (nextProps.setupThemeError !== undefined) {
        this.setState({ activeModalError: true });
      }
      // Success
      // Go to success page for supported and un-supported theme
      else if (nextProps.setupThemeSuccess) {
        const { theme, inputThemeName, outputThemeName } = nextProps;
        const isUnsupported = inputThemeName !== 'default' && outputThemeName === 'default';
        const historyState = {
          themeId: theme ? theme.value : '',
          themeName: theme ? theme.label : '',
          published: theme ? theme.published : false
        };
        if (isUnsupported) {
          history.push({
            pathname: routes.unsupportedThemeSetupSuccess.view(),
            state: historyState
          });
        } else {
          history.push({
            pathname: routes.supportedThemeSetupSuccess.view(),
            state: historyState
          });
        }
      }
    }
  }

  componentWillUnmount() {
    const { unmountComponent } = this.props;
    unmountComponent();
  }

  render() {
    const { dataSetup, activeModalError, activeModalConfirmSetup, step } = this.state;
    const {
      i18n,
      theme,
      themes,
      isTrialTheme,
      inputThemeName,
      outputThemeName,
      isOS20,
      handleSubmit,
      pristine,
      submitting,
      checkThemeLoading,
      setupThemeLoading,
      getAllThemesLoading,
      checkThemeCompatibleLoading,
      themeSetupV2,
      themeCheck,
      tenantApp
    } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    const disableThemeSetup = this.checkDisableThemeSetup();
    const themesList = themes
      ? themes.map(item => ({
          value: item.id,
          label: `${item.name}${
            item.role === 'main'
              ? i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.suffixThemeLive')
              : ''
          }`,
          published: item.role === 'main'
        }))
      : [];
    const choices = [
      {
        label: i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.themeLeft'),
        value: 'v',
        renderChildren: () => (
          <Link external url={URL['FilterVerticalDemo']}>
            {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.viewDemo')}
            <Icon source={ExternalMinor} color="interactive" />
          </Link>
        )
      },
      {
        label: i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.themeTop'),
        value: 'h',
        renderChildren: () => (
          <Link external url={URL['FilterHorizontalDemo']}>
            {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.viewDemo')}
            <Icon source={ExternalMinor} color="interactive" />
          </Link>
        )
      }
    ];

    // Theme setup v1, select theme OS 2.0
    const warningOS20 =
      !isTrialTheme && !themeSetupV2 && isOS20
        ? i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.warningOS20', {
            nt_break_line: <br />
          })
        : '';

    const warning = !isTrialTheme &&
      inputThemeName !== 'default' &&
      outputThemeName === 'default' && (
        <span>
          {warningOS20}
          {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.warning', {
            nt_break_line: <br />
          })}
        </span>
      );

    const info = !isTrialTheme &&
      inputThemeName === outputThemeName &&
      !_.isEmpty(inputThemeName) && (
        <TextStyle variation="positive">
          {warningOS20}
          {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.info')}
        </TextStyle>
      );

    const renderInfoAndLayout = (
      <>
        {info && <div className="inline-info">{info}</div>}
        <div className="BC-Theme-Setup_Form-Item" style={{ paddingTop: '0.5625rem' }}>
          <div className="title">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step2_SelectPosition')}
            </TextStyle>
          </div>
          <Field
            name="layout"
            choices={choices}
            component={RenderChoice}
            isDisabled={
              _.isEmpty(themesList) ||
              checkThemeLoading ||
              checkThemeCompatibleLoading ||
              setupThemeLoading
            }
          />
        </div>
      </>
    );

    return (
      <Card
        title={
          <div className="BC_Card-Header">
            <Heading>
              <span style={{ display: 'inline-flex' }}>
                {step !== 0 && (
                  <span style={{ marginRight: '0.625rem', marginTop: '2px' }}>
                    <Button
                      icon={ChevronLeftMinor}
                      plain
                      onClick={this.backStep}
                      disabled={setupThemeLoading}
                    />
                  </span>
                )}
                <span>{i18n.translate(`AdminHTML.ThemeSetup.ThemeSetup.step${step}_Title`)}</span>
              </span>
            </Heading>
            {step !== 0 && (
              <div className="step-progress-setup">
                <StepProgress step={step} />
              </div>
            )}
          </div>
        }>
        {step === 0 ? (
          <Card.Section>
            <div className="BC-Page BC-Page__Text">
              <TextStyle>
                {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step0_Content', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step0_Content_t1')}
                    </TextStyle>
                  )
                })}
              </TextStyle>
            </div>
            <Button primary onClick={this.updateStep} disabled={disableThemeSetup}>
              {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step0_Button')}
            </Button>
          </Card.Section>
        ) : step === 1 ? (
          <Card.Section>
            <div className="BC-Page BC-Page__Text">
              <TextStyle>
                {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step1_Content01')}
              </TextStyle>
            </div>
            <div className="BC-Page BC-Page__Text">
              <TextStyle>
                {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step1_Content02', {
                  t1: (
                    <Link external url={URL['DuplicatingThemesDoc']}>
                      {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step1_Content02_t1')}
                    </Link>
                  )
                })}
              </TextStyle>
            </div>
            <Button primary onClick={this.updateStep}>
              {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step1_Button')}
            </Button>
          </Card.Section>
        ) : (
          <Card.Section>
            <form className="BC-Theme-Setup_Form BC-Theme-Setup_Form-V2">
              <FormLayout>
                <div className="BC-Theme-Setup_Form-Item">
                  <div className="title">
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.step2_SelectTheme')}
                    </TextStyle>
                  </div>
                  {getAllThemesLoading ? (
                    <SkeletonDisplayText size="large" />
                  ) : (
                    <React.Fragment>
                      <Field
                        name="theme"
                        component={RenderSelect2}
                        options={themesList}
                        placeholder={i18n.translate(
                          'AdminHTML.ThemeSetup.ThemeSetup.step2_Theme_Placeholder'
                        )}
                        placeholderEmpty={i18n.translate(
                          'AdminHTML.ThemeSetup.ThemeSetup.step2_Theme_PlaceholderEmpty'
                        )}
                        isDisabled={
                          _.isEmpty(themesList) ||
                          checkThemeLoading ||
                          checkThemeCompatibleLoading ||
                          setupThemeLoading
                        }
                      />
                      {warning && (
                        <div className="inline-warning">
                          <InlineError message={warning} />
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>

                {enableAppVersionSwitching &&
                  (checkThemeLoading || checkThemeCompatibleLoading) && (
                    <SkeletonBodyText lines={3} />
                  )}

                {enableAppVersionSwitching && themeCheck && isThemeTurbo(themeCheck) && (
                  <div style={{ marginTop: '-15px' }}>
                    <ThemeTurboWarning type={WarningType.IN_THEME_SETUPV3_THEME_TURBO} />
                  </div>
                )}
                {enableAppVersionSwitching &&
                  themeCheck &&
                  !isThemeTurbo(themeCheck) &&
                  renderInfoAndLayout}
                {!enableAppVersionSwitching && renderInfoAndLayout}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    primary
                    onClick={handleSubmit(this.handleRunSetup.bind(this))}
                    loading={setupThemeLoading}
                    disabled={
                      pristine ||
                      submitting ||
                      checkThemeLoading ||
                      checkThemeCompatibleLoading ||
                      getAllThemesLoading ||
                      isTrialTheme ||
                      (themeCheck && isThemeTurbo(themeCheck)) ||
                      _.isEmpty(theme)
                    }>
                    {i18n.translate('AdminHTML.Common.Action.runSetup')}
                  </Button>
                  {setupThemeLoading && (
                    <span style={{ color: 'var(--p-interactive)', marginLeft: '16px' }}>
                      {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.setupRunning')}
                    </span>
                  )}
                </div>
              </FormLayout>
            </form>
          </Card.Section>
        )}
        <ConfirmRunSetupModal
          data={dataSetup}
          active={activeModalConfirmSetup}
          handleChange={() => this.toggleChangeState('activeModalConfirmSetup')}
          handleContinue={this.runSetup}
        />
        <ThemeErrorModal
          active={activeModalError}
          handleChange={() => this.toggleChangeState('activeModalError')}
        />
      </Card>
    );
  }

  // Toggle show confirm button
  updateStep = () => {
    const { step } = this.state;
    const { getAllThemes } = this.props;

    this.setState({ step: step + 1 });
    if (step === 1) {
      getAllThemes();
    }
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  // Handle validate data before submit
  validateFormData(data) {
    const { i18n } = this.props;
    if (!data || _.isEmpty(data.layout)) {
      throw new SubmissionError({
        layout: i18n.translate('AdminHTML.ThemeSetup.Validate.emptyLayout')
      });
    }
    return true;
  }

  //Handle submit
  handleRunSetup = data => {
    if (this.validateFormData(data)) {
      const { theme } = data;
      if (theme.published) {
        this.setState({ activeModalConfirmSetup: true, dataSetup: data });
      } else {
        this.runSetup(data);
      }
    }
  };

  // Run Setup
  runSetup = async data => {
    const { setupTheme, trackEvent } = this.props;
    const { activeModalConfirmSetup } = this.state;

    // Handle if the confirm modal is active
    if (activeModalConfirmSetup) {
      this.setState({ activeModalConfirmSetup: false });
    }

    // Run setup
    let prettyData = { themeId: data.theme.value, layout: data.layout[0] };
    await setupTheme(prettyData);

    await trackEvent('csatFeedback', 'themeSetup', 'csatFeedback_themeSetup');
  };

  checkDisableThemeSetup = () => {
    const { subscription, tenantApp } = this.props;
    const { status } = subscription;
    const externalInfo = _.get(tenantApp, 'externalInfo', {});
    const planName = _.get(externalInfo, 'plan_name', '');

    // Check not dev stores
    const isPaymentPlan = checkShopifyPaidPlan(planName);
    return (
      (status === 'expired' || status === 'regularExpired' || status === 'exceeded') &&
      isPaymentPlan
    );
  };

  backStep = () => {
    const { step } = this.state;
    this.setState({ step: step - 1 });
  };
}

// Add sync validate to call check theme api whenever change theme field
const asyncValidate = async (values, dispatch, props) => {
  const value = _.get(values, 'theme.value', '');
  if (value) {
    dispatch(themeSetupV3Action.checkThemeWithTurbo(value, props.i18n));
    const result = await dispatch(themeSetupAction.checkTheme(value, props.i18n));
    if (result.type === 'CHECK_THEME_SUCCESS') {
      const { isTrialTheme } = result.payload.data;
      if (isTrialTheme) {
        const { i18n } = props;
        const throwData = {
          theme: (
            <span>
              {i18n.translate('AdminHTML.ThemeSetup.Validate.trialTheme', {
                nt_break_line: <br />
              })}
            </span>
          )
        };
        throw throwData;
      }
    }
  }
};

const ThemeSetupForm = reduxForm({
  form: 'ThemeSetup', // a unique identifier for this form
  enableReinitialize: true,
  asyncValidate,
  asyncChangeFields: ['theme'],
  shouldAsyncValidate: params => {
    return params.trigger === 'change' && params.syncValidationPasses; // do not async validate on submit
  }
})(ThemeSetup);

const mapStateToProps = (state, props) => {
  const { themeSetup = {}, oauth = {}, themeSetupV3 = {} } = state;
  const formValues = getFormValues('ThemeSetup')(state);
  const themeSetupV2 = _.get(oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    theme: formValues && formValues.theme ? formValues.theme : {},
    themes: _.get(themeSetup, 'allThemes', []),
    getAllThemesLoading: _.get(themeSetup, 'getAllThemesLoading', true),
    isTrialTheme: _.get(themeSetup, 'isTrialTheme', false),
    inputThemeName: _.get(themeSetup, 'inputThemeName', ''),
    outputThemeName: _.get(themeSetup, 'outputThemeName', ''),
    isOS20: _.get(themeSetup, 'isOS20', false),
    themeSetupV2,
    checkThemeLoading: _.get(themeSetup, 'checkThemeLoading', false),
    setupThemeLoading: _.get(themeSetup, 'setupThemeLoading', false),
    setupThemeSuccess: _.get(themeSetup, 'setupThemeSuccess', false),
    setupThemeError: _.get(themeSetup, 'setupThemeError', undefined),
    subscription: _.get(state.oauth, 'subscription', {}),
    tenantApp,
    checkThemeCompatibleLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    themeCheck: _.get(themeSetupV3, 'themeCheck', null)
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(themeSetupAction.getAllThemes(ownProps.i18n)),
  setupTheme: (data: { theme: string, layout: string }) => {
    const meta = {
      onSuccess: responseData => {
        const { data } = responseData.data;
        const { upgradeUsingThemeSetup } = data;
        const res = {
          upgradeThemeV2: {
            upgradeUsingThemeSetup
          }
        };
        dispatch(updateLocalTenantApp(res));
      }
    };
    dispatch(themeSetupAction.setupTheme(data, meta, ownProps.i18n));
  },
  unmountComponent: () => dispatch(themeSetupAction.unmountComponent()),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ThemeSetupForm));
